import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Save, Loader2, ArrowLeft, DollarSign, Package, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BillingCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deliveryNum = searchParams.get('delivery');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [salesOrderItems, setSalesOrderItems] = useState({});

  const [header, setHeader] = useState({
    delivery_number: deliveryNum || '',
    billing_date: new Date().toISOString().split('T')[0],
    payment_terms: '',
    header_note: ''
  });

  useEffect(() => {
    loadDeliveries();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (header.delivery_number) {
      loadDeliveryDetails(header.delivery_number);
    }
  // eslint-disable-next-line
  }, [header.delivery_number]);

  const loadDeliveries = async () => {
    setIsLoading(true);
    try {
      // Get deliveries with GOODS_ISSUED status (ready for billing)
      const response = await axios.get(`${BACKEND_URL}/api/otc/deliveries?status=GOODS_ISSUED`);
      setDeliveries(response.data || []);
      
      // If delivery number was passed, load it
      if (deliveryNum) {
        const delivery = response.data.find(d => d.delivery_number === deliveryNum);
        if (delivery) {
          setSelectedDelivery(delivery);
          await loadSalesOrderPricing(delivery);
        }
      }
    } catch (error) {
      console.error('Error loading deliveries:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDeliveryDetails = async (delNum) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/deliveries/${delNum}`);
      setSelectedDelivery(response.data);
      await loadSalesOrderPricing(response.data);
    } catch (error) {
      console.error('Error loading delivery details:', error);
      toast.error('Failed to load delivery details');
    }
  };

  const loadSalesOrderPricing = async (delivery) => {
    if (!delivery || !delivery.sales_order_number) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/sales-orders/${delivery.sales_order_number}`);
      const soItems = {};
      (response.data.items || []).forEach(item => {
        soItems[item.item_number] = item;
      });
      setSalesOrderItems(soItems);
    } catch (error) {
      console.error('Error loading sales order pricing:', error);
    }
  };

  const calculateItemPrice = (deliveryItem) => {
    const soItem = salesOrderItems[deliveryItem.sales_order_item];
    if (!soItem) return { net_value: 0, tax_amount: 0, gross_value: 0 };
    
    // Calculate proportional pricing based on delivery quantity
    const ratio = deliveryItem.delivery_quantity / soItem.order_quantity;
    return {
      net_price: soItem.net_price || 0,
      net_value: (soItem.net_value || 0) * ratio,
      tax_amount: (soItem.tax_amount || 0) * ratio,
      gross_value: (soItem.gross_value || 0) * ratio,
      currency: soItem.currency || 'USD'
    };
  };

  const calculateTotals = () => {
    if (!selectedDelivery) return { net: 0, tax: 0, gross: 0 };
    
    let net = 0, tax = 0, gross = 0;
    (selectedDelivery.items || []).forEach(item => {
      const pricing = calculateItemPrice(item);
      net += pricing.net_value;
      tax += pricing.tax_amount;
      gross += pricing.gross_value;
    });
    
    return { net, tax, gross };
  };

  const validateForm = () => {
    if (!header.delivery_number) {
      toast.error('Please select a Delivery');
      return false;
    }
    if (!header.billing_date) {
      toast.error('Billing Date is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Use the convenience endpoint to create billing from delivery
      const response = await axios.post(`${BACKEND_URL}/api/otc/billing-documents/from-delivery/${header.delivery_number}`);
      toast.success(`Billing Document '${response.data.billing_number}' created successfully!`);
      navigate(`/otc/billing/${response.data.billing_number}`);
    } catch (error) {
      console.error('Error creating billing document:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create billing document: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const totals = calculateTotals();

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/billing')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Billing Document</h1>
              <p className="text-sm text-gray-600">Create customer invoice from delivery</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving || !selectedDelivery} data-testid="save-billing-btn">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Billing
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Selection */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Billing Header
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Delivery *</Label>
                  <Select 
                    value={header.delivery_number || '__SELECT__'} 
                    onValueChange={(v) => setHeader({...header, delivery_number: v === '__SELECT__' ? '' : v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select Delivery" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Delivery</SelectItem>
                      {deliveries.map(del => (
                        <SelectItem key={del.delivery_number} value={del.delivery_number}>
                          {del.delivery_number} - SO: {del.sales_order_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {deliveries.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">No deliveries with Goods Issue posted available</p>
                  )}
                </div>
                <div>
                  <Label>Billing Date *</Label>
                  <Input
                    type="date"
                    value={header.billing_date}
                    onChange={(e) => setHeader({...header, billing_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={header.payment_terms || '__NONE__'} onValueChange={(v) => setHeader({...header, payment_terms: v === '__NONE__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">-- None --</SelectItem>
                      <SelectItem value="NT30">Net 30 Days</SelectItem>
                      <SelectItem value="NT60">Net 60 Days</SelectItem>
                      <SelectItem value="NT90">Net 90 Days</SelectItem>
                      <SelectItem value="COD">Cash on Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Header Note</Label>
                  <Input
                    value={header.header_note}
                    onChange={(e) => setHeader({...header, header_note: e.target.value})}
                    placeholder="Additional notes..."
                    maxLength={500}
                  />
                </div>
              </div>
            </Card>

            {/* Items Preview */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Billing Items Preview
              </h3>
              
              {!selectedDelivery ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Select a Delivery to see billing items</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 text-sm font-semibold">Item</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Material</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Description</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Quantity</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Net Price</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Net Value</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Tax</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Gross Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedDelivery.items || []).map((item, idx) => {
                        const pricing = calculateItemPrice(item);
                        return (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono">{(idx + 1) * 10}</td>
                            <td className="py-2 px-3">{item.material_id || '-'}</td>
                            <td className="py-2 px-3">{item.item_description}</td>
                            <td className="py-2 px-3 text-right">{item.delivery_quantity} {item.unit_of_measure}</td>
                            <td className="py-2 px-3 text-right font-mono">{pricing.currency} {pricing.net_price?.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-mono">{pricing.currency} {pricing.net_value?.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-mono text-red-600">{pricing.currency} {pricing.tax_amount?.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold">{pricing.currency} {pricing.gross_value?.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Delivery Info */}
            {selectedDelivery && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Delivery Info</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Delivery</p>
                    <p className="font-mono font-bold text-orange-600">{selectedDelivery.delivery_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sales Order</p>
                    <p className="font-mono">{selectedDelivery.sales_order_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ship-to Party</p>
                    <p className="font-medium">{selectedDelivery.ship_to_party}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sales Organization</p>
                    <p className="font-medium">{selectedDelivery.sales_organization}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">GI Date</p>
                    <p className="font-medium">{selectedDelivery.actual_goods_issue_date?.split('T')[0]}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Totals */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Billing Totals</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Value:</span>
                  <span className="font-mono">USD {totals.net.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="font-mono text-red-600">+ USD {totals.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t">
                  <span className="font-semibold">Gross Value:</span>
                  <span className="font-mono font-bold text-lg">USD {totals.gross.toFixed(2)}</span>
                </div>
              </div>
            </Card>

            {/* Help */}
            <Card className="p-6 bg-orange-50">
              <h3 className="text-sm font-semibold text-orange-800 mb-2">Billing Process</h3>
              <ul className="text-xs text-orange-700 space-y-1">
                <li>1. Select a delivery with GI posted</li>
                <li>2. Review billing items and prices</li>
                <li>3. Click "Create Billing"</li>
                <li>4. Release billing for accounting</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BillingCreate;
